import { useEffect, useState } from 'react';
import { Input, type InputProps } from './input';
import { formatNumeric, parseNumeric } from '../lib/numbers';

export interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
  /** Truncate to a whole number. */
  integer?: boolean;
  /** When false, a cleared field commits 0 rather than null. */
  nullable?: boolean;
}

/**
 * A numeric field that keeps what the user typed.
 *
 * The stored value is a number, but the *input* holds raw text until it parses.
 * Parsing straight into the value — the pattern this replaces — makes decimals
 * impossible to type, because the value round-trips through `Number('12.')` and
 * comes back as `12` on the keystroke that adds the point.
 *
 * Deliberately `type="text"` with `inputMode`: a `type="number"` input reports
 * an empty string for intermediate states in several browsers, which loses the
 * text just as effectively.
 */
export const NumberInput = ({
  value,
  onValueChange,
  integer = false,
  nullable = true,
  ...props
}: NumberInputProps) => {
  const [draft, setDraft] = useState(() => formatNumeric(value));

  // Re-sync only when the incoming value is genuinely different from what the
  // draft already represents — otherwise typing "12." would be rewritten to
  // "12" by the very round-trip this component exists to avoid.
  useEffect(() => {
    if (parseNumeric(draft, integer) !== (value ?? null)) {
      setDraft(formatNumeric(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      {...props}
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      value={draft}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        const parsed = parseNumeric(raw, integer);
        onValueChange(parsed === null && !nullable ? 0 : parsed);
      }}
    />
  );
};
